import type { Layer, PixelOp } from "#canvas/types.ts";

const HISTORY_CAP = 50;

function cloneLayer(l: Layer): Layer {
  return { ...l, data: l.data.map((row) => row.slice()) };
}

function cloneLayers(layers: Layer[]): Layer[] {
  return layers.map(cloneLayer);
}

function layersEqual(a: Layer[], b: Layer[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const la = a[i];
    const lb = b[i];
    if (!la || !lb) return false;
    if (la.id !== lb.id || la.visible !== lb.visible) return false;
    if (la.data === lb.data) continue;
    if (la.data.length !== lb.data.length) return false;
    for (let r = 0; r < la.data.length; r++) {
      const ra = la.data[r];
      const rb = lb.data[r];
      if (!ra || !rb) return false;
      if (ra === rb) continue;
      if (ra.length !== rb.length) return false;
      for (let c = 0; c < ra.length; c++) {
        if (ra[c] !== rb[c]) return false;
      }
    }
  }
  return true;
}

export function applyOps(layer: Layer, ops: PixelOp[]): Layer {
  const data = layer.data.map((row) => row.slice());
  for (const op of ops) {
    const row = data[op.row];
    if (!row) continue;
    if (op.col < 0 || op.col >= row.length) continue;
    row[op.col] = op.color;
  }
  return { ...layer, data };
}

export interface LayerHistory {
  push: (snapshot: Layer[]) => void;
  pushIfChanged: (snapshot: Layer[]) => void;
  undo: () => Layer[] | null;
  redo: () => Layer[] | null;
  reset: (snapshot: Layer[]) => void;
}

export function createLayerHistory(initial: Layer[]): LayerHistory {
  let stack: Layer[][] = [cloneLayers(initial)];
  let idx = 0;

  const push = (snapshot: Layer[]) => {
    stack = stack.slice(0, idx + 1);
    stack.push(cloneLayers(snapshot));
    if (stack.length > HISTORY_CAP) stack.shift();
    else idx++;
  };

  return {
    push,
    pushIfChanged(snapshot) {
      const top = stack[idx];
      if (!top || !layersEqual(top, snapshot)) push(snapshot);
    },
    undo() {
      if (idx === 0) return null;
      idx--;
      const snap = stack[idx];
      return snap ? cloneLayers(snap) : null;
    },
    redo() {
      if (idx >= stack.length - 1) return null;
      idx++;
      const snap = stack[idx];
      return snap ? cloneLayers(snap) : null;
    },
    reset(snapshot) {
      stack = [cloneLayers(snapshot)];
      idx = 0;
    },
  };
}
